/*
 * Copyright (c) Microsoft Corporation. All rights reserved. Licensed under the MIT Licence.
 * See LICENSE in the project root for licence information.
 */

/* global Office */

import { AiDraftOption } from "../services/ai.service";
import { validatePrerequisites, executeAiDraft } from "./commands.utils";

Office.onReady((info) => {
  console.log("Office.js initialized:", info.host, info.platform);
});

async function handleAiAction(option: AiDraftOption, event: Office.AddinCommands.Event) {
  console.log(`[AILA] ${option} action triggered at ${new Date().toISOString()}`);
  let didComplete = false;
  const completeEvent = () => {
    if (!didComplete) {
      console.log(`[AILA] ${option} event.completed() called`);
      event.completed();
      didComplete = true;
    } else {
      console.warn(`[AILA] ${option} event.completed() already called, skipping`);
    }
  };
  try {
    const captured = await validatePrerequisites(option, completeEvent);
    if (captured == null) return;
    await executeAiDraft(option, captured, completeEvent);
  } catch (error) {
    console.error(`[AILA] Unhandled error in ${option}:`, error);
    completeEvent();
  }
}

async function writeClause(event: Office.AddinCommands.Event) {
  await handleAiAction(AiDraftOption.WriteClause, event);
}

async function writeDefinition(event: Office.AddinCommands.Event) {
  await handleAiAction(AiDraftOption.WriteDefinition, event);
}

async function makeShorter(event: Office.AddinCommands.Event) {
  await handleAiAction(AiDraftOption.MakeShorter, event);
}

async function makeLonger(event: Office.AddinCommands.Event) {
  await handleAiAction(AiDraftOption.MakeLonger, event);
}

async function simplifyLanguage(event: Office.AddinCommands.Event) {
  await handleAiAction(AiDraftOption.SimplifyLanguage, event);
}

async function complexifyLanguage(event: Office.AddinCommands.Event) {
  await handleAiAction(AiDraftOption.ComplexifyLanguage, event);
}

async function summarise(event: Office.AddinCommands.Event) {
  await handleAiAction(AiDraftOption.Summarise, event);
}

async function draftFullDocument(event: Office.AddinCommands.Event) {
  await handleAiAction(AiDraftOption.DraftFullDocument, event);
}

async function draftOutline(event: Office.AddinCommands.Event) {
  await handleAiAction(AiDraftOption.DraftOutline, event);
}

// Register the functions with Office.
Office.actions.associate("writeClause", writeClause);
Office.actions.associate("writeDefinition", writeDefinition);
Office.actions.associate("makeShorter", makeShorter);
Office.actions.associate("makeLonger", makeLonger);
Office.actions.associate("simplifyLanguage", simplifyLanguage);
Office.actions.associate("complexifyLanguage", complexifyLanguage);
Office.actions.associate("summarise", summarise);
Office.actions.associate("draftFullDocument", draftFullDocument);
Office.actions.associate("draftOutline", draftOutline);
